import {
  createTheme,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from "@mui/material";
import React from "react";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export const mediaQuery = {
  desktop: 1000,
  mobile: 999,
};

export const defaultOLTheme = {
  // gray
  gray800: "#2E2E2E",
  gray600: "#5B5B5C",
  gray500: "#727273",
  gray400: "#8E8E8F",
  gray300: "#AAAAAB",
  gray200: "#E3E3E3",
  gray100: "#EBEBEB",
  gray50: "#F7F7F7",
  // primary
  primary800: "#130F3A",
  primary700: "#30265D",
  primary600: "#4A3C88",
  primary500: "#5C4BAA",
  primary400: "#7D6FBB",
  primary200: "#BEB7DD",
  primary100: "#DEDBEE",
  primary50: "#EFEDF6",
  // white
  white800: "#79A3CE",
  white500: "#FAFCFE",
  white50: "#FEFFFF",
  white00: "#FFFFFF",
  // green
  green800: "#1E4714",
  green300: "#93D084",
  green500: "#4BB132",
  green50: "#EDF7EA",
  // red
  red800: "#6C2525",
  red600: "#C04646",
  red500: "#EB5757",
  red300: "#F39A9A",
  red100: "#FBDDDD",
  red50: "#FDEEEE",
  // black
  black: "#000000",
  // orange
  orange800: "#6C431E",
  orange700: "#99602D",
  orange600: "#C57C3B",
  orange500: "#F2994A",
  orange300: "#F7C292",
  orange200: "#FAD6B7",
  orange50: "#FCEBDB",
  // purple
  purple: "#B7279F",
};

export const theme = createTheme({
  palette: {
    primary: {
      main: "#5C4BAA",
    },
    secondary: {
      main: "#727273",
    },
    success: {
      main: "#4BB132",
    },
    error: {
      main: "#EB5757",
    },
    background: {
      default: "#FAFCFE",
    },
  },
  spacing: 2,
  typography: {
    fontFamily: `"Inter", sans-serif`,
    allVariants: {
      fontFamily: `"Inter", sans-serif`,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ".MuiMultiSectionDigitalClockSection-item.Mui-selected": {
          borderRadius: "4px",
        },
        ".MuiPickersDay-root": {
          borderRadius: "4px !important",
          "&:hover": {
            borderRadius: "4px",
          },
        },
        ".MuiPickersArrowSwitcher-root": {
          gap: "24px",
        },
        ".MuiPickersArrowSwitcher-button": {
          "& > svg": {
            fill: defaultOLTheme.gray800,
          },
        },
        ".MuiPickersLayout-actionBar": {
          borderTop: `1px solid ${defaultOLTheme.gray200}`,
        },
        ".MuiPickersLayout-contentWrapper": {
          "& > hr": {
            display: "none",
          },
        },
        body: {
          height: "100%",
          fontFamily: '"Inter", sans-serif',
          "& #root": {
            minHeight: "100%",
            height: "100%",
          },
          "& *": {
            fontFamily: '"Inter", sans-serif',
          },
        },
        html: {
          height: "100%",
          "*": {
            scrollbarColor: "rgba(0,0,0,0.5) transparent",
            scrollbarWidth: "thin",
            // chrome, safari
            "&::-webkit-scrollbar-button": {
              display: "none",
            },
            "&::-webkit-scrollbar": {
              backgroundColor: "transparent",
              width: "8px",
              height: "8px",
              scrollbarGutter: "auto",
            },
            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
              borderRadius: 8,
              backgroundColor: "rgba(0,0,0,0.5)",
            },
            "&:hover::-webkit-scrollbar-thumb": {
              visibility: "visible",
            },
            "input[type=number]::-webkit-inner-spin-button": {
              display: "none",
            },
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "[dir='rtl'] &": {
            transformOrigin: "right !important",
            left: "inherit !important",
            right: "28px !important",
            overflow: "unset",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "[dir='rtl'] &": {
            textAlign: "right",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "[dir='rtl'] & legend": {
            textAlign: "right",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          "[dir='rtl'] &": {
            paddingRight: "14px !important",
            paddingLeft: "65px !important",
          },
        },
        endAdornment: {
          "[dir='rtl'] &": {
            right: "unset !important",
            left: 0,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "[dir='rtl'] &": {
            paddingRight: "14px",
            paddingLeft: "32px",
          },
        },
        icon: {
          "[dir='rtl'] &": {
            right: "unset !important",
            left: "8px",
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(2px)",
        },
        invisible: {
          backdropFilter: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "15px",
          fontWeight: 500,
          textTransform: "none",
          letterSpacing: "0.46px",
          borderRadius: "6px",
          textWrap: "nowrap",
          whiteSpace: "nowrap",
          ":disabled": {
            backgroundColor: defaultOLTheme.gray300,
            color: defaultOLTheme.gray600,
            cursor: "not-allowed",
            pointerEvents: "auto",
            border: 0,
            "&:hover": {
              backgroundColor: defaultOLTheme.gray300,
              border: "none",
              color: defaultOLTheme.gray600,
            },
          },
        },
        sizeLarge: {
          padding: "9px 22px",
          fontSize: "16px",
          lineHeight: "26px",
        },
        sizeMedium: {
          padding: "6px 8px",
          fontSize: "14px",
          lineHeight: "24px",
        },
        sizeSmall: {
          padding: "3px 16px",
          fontSize: "16px",
          lineHeight: "26px",
        },
        contained: {
          boxShadow:
            "0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20) !important",
        },
        outlinedSecondary: {
          boxShadow: "none !important",
        },
        containedSecondary: {
          boxShadow: "none !important",
        },
        outlined: {
          fontWeight: 500,
          color: defaultOLTheme.gray800,
          background: defaultOLTheme.white50,
          border: `1px solid ${defaultOLTheme.gray200}`,
          ":hover": {
            color: defaultOLTheme.gray800,
            border: `1px solid ${defaultOLTheme.gray200}`,
            background: "#FEFEFF",
          },
        },
        textSizeLarge: {
          padding: "8px 0",
          ":disabled": {
            backgroundColor: "inherit",
            color: defaultOLTheme.gray300,
          },
        },
        textSizeMedium: {
          padding: "6px 0",
          ":disabled": {
            backgroundColor: "inherit",
            color: defaultOLTheme.gray300,
          },
        },
        textSizeSmall: {
          padding: "4px 0",
          ":disabled": {
            backgroundColor: "inherit",
            color: defaultOLTheme.gray300,
          },
        },
        startIcon: {
          marginRight: "10px",
          marginLeft: 0,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          [`@media (max-width: ${mediaQuery.mobile}px)`]: {
            display: "none",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          [`@media (max-width: ${mediaQuery.mobile}px)`]: {
            display: "none",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          [`@media (max-width: ${mediaQuery.mobile}px)`]: {
            display: "none",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          [`@media (max-width: ${mediaQuery.mobile}px)`]: {
            display: "none",
          },
        },
      },
    },
  },
});

export type OLTheme = Theme & typeof defaultOLTheme;

type Props = {
  children: React.ReactNode;
};

export function OLThemeProvider({ children }: Props) {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <ThemeProvider
          theme={(muiTheme) => ({ ...muiTheme, ...defaultOLTheme })}
        >
          {children}
        </ThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
